import { Avatar, Button, FlexBox, Tag, Tooltip } from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import classnames from 'classnames';
import { Tag as TagT } from 'lib-shared-legacy-types';
import { useState } from 'react';

import styles from './conversationPreviewTags.module.scss';

type ConversationPreviewTagsProps = {
  tags: readonly TagT[];
  scrollable?: boolean;
  className?: string;
  showSectionIcon?: boolean;
  numberOfTagsToShowInitially?: number;
};

export const ConversationPreviewTags = ({
  tags,
  scrollable = false,
  className,
  showSectionIcon,
  numberOfTagsToShowInitially = 10,
}: ConversationPreviewTagsProps): JSX.Element | null => {
  const numberOfTags = tags?.length;
  const showToggleButton = numberOfTagsToShowInitially < numberOfTags;

  const [showAllTags, setShowAllTags] = useState(false);
  const rootClass = classnames(
    styles.root,
    {
      [styles.rootScrollable]: scrollable,
    },
    className,
  );

  const toggleShowAll = () => {
    setShowAllTags(!showAllTags);
  };

  const tagsToRender = tags.slice(
    0,
    showAllTags ? numberOfTags : numberOfTagsToShowInitially,
  );
  const numberOfTagsLeftToRender = numberOfTags - tagsToRender.length;
  const buttonLabel = !showAllTags
    ? `+ ${numberOfTagsLeftToRender} more`
    : `Show less`;

  if (!numberOfTags) {
    return null;
  }

  return (
    <FlexBox
      className={rootClass}
      flexDirection="row"
      gap="200"
      alignItems="center"
    >
      {showSectionIcon && (
        <Tooltip content="Tags">
          <Avatar
            className={styles.icon}
            icon="tag"
            color={{
              contentColor: colors.neutral[700],
              backgroundColor: colors.neutral[100],
            }}
          />
        </Tooltip>
      )}

      <FlexBox
        alignItems="center"
        gap="200"
        flexWrap={scrollable ? 'nowrap' : 'wrap'}
      >
        {tagsToRender.map((tag) => {
          const isTruncated = tag.name.length > 17;

          return (
            <Tooltip key={tag.id} content={tag.name} disabled={!isTruncated}>
              <Tag
                color={tag.color}
                className={styles.tag}
                disabled={tag.is_deactivated}
              >
                {tag.name}
              </Tag>
            </Tooltip>
          );
        })}
      </FlexBox>

      {showToggleButton && (
        <Button variant="tertiary" onClick={toggleShowAll}>
          {buttonLabel}
        </Button>
      )}
    </FlexBox>
  );
};
